import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { APP_NAME, ENGINE_LABELS } from "@/config";
import { cn } from "@/lib/utils";
import {
  Page, PageHeader, Card, Chip, Tag, StatusBadge, Segmented, StatStrip, SectionTitle, PrivacyNote, Loading, Lines,
  EngineBadge, usd, int, plural, ago,
} from "@/components/common";
import { Button } from "@/components/ui/button";
import InvestigateIntegrity from "@/components/InvestigateIntegrity";
import InvestigateReports from "@/components/InvestigateReports";
import { ArrowLeft, ChevronRight, ChevronDown, TriangleAlert, CircleX, CircleDashed, Check, X, Info, ShieldX } from "lucide-react";

// Investigate (docs/INVESTIGATE_PLAN.md): one session's event log as a timeline grouped by turn, the findings that
// point into it, and whether the log is whole. The founder and the session's independent professional see the same
// page (GET /sessions/{id}/investigation and /events). Metadata only: names, outcomes, counts, times, paths, SHAs and
// money, never what was said or what a command contained.

export const investigatePath = (role, sid) =>
  role === "coder" ? `/team/sessions/${sid}/timeline` : `/founder/sessions/${sid}/investigate`;

// backend/session_events.py ERRORS, in words
export const ERROR_WORDS = {
  session_locked: "The session was locked",
  runner_offline: "The workspace server was offline",
  runner_timeout: "The workspace server stopped answering",
  runner_outdated: "The workspace server needs an update",
  key_unavailable: "The session's key wasn't available",
  push_failed: "The push to the project's repository failed",
  mcp_unavailable: "An MCP server didn't start",
  budget_reached: "Budget reached",
  credits: `Out of ${APP_NAME} credits`,
  internal: `Something went wrong in ${APP_NAME}`,
  engine_missing: "The engine isn't installed on the workspace server",
  engine_error: "The engine stopped with an error",
  turn_failed: "The engine reported the turn as failed",
  not_unlocked: "The session wasn't unlocked on the workspace server",
  refused: "The workspace server can't run this engine",
  runner_error: "The workspace server hit an error",
  other: "An error",
};
// REFUSALS: why the metering proxy refused a model call
export const REFUSAL_WORDS = {
  model_blocked: "Model not allowed",
  call_blocked: "Provider call not allowed",
  background: "Background request",
  budget_reached: "Budget reached",
  credits: `Out of ${APP_NAME} credits`,
  other: "Refused",
};
// SESSION_ACTIONS
const SESSION_WORDS = {
  unlocked: "Unlocked", locked: "Locked", ended: "Ended", submitted: "Submitted for review",
  report_built: "Change report built", stop_requested: "Stop pressed", other: "Session changed",
};
// STOP_REASONS: why a model call ended
const STOP_WORDS = {
  end_turn: "end of turn", tool_use: "tool use", max_tokens: "output limit", stop_sequence: "stop sequence",
  pause_turn: "paused", refusal: "refused", context_window: "context full", content_filter: "content filter",
  completed: "completed", incomplete: "incomplete", failed: "failed", cancelled: "cancelled", other: "other",
};
// VIAS: a model call the platform made for the session, billed to it
const VIA_WORDS = { report_summary: "Change report summary", ask_documents: "Ask your documents", other: "A call for the session" };
// change_reports.SENSITIVE, as "Edited …"
const SENSITIVE_WORDS = {
  "CI/CD pipeline": "a CI/CD pipeline file", dependencies: "a dependency file",
  "build, deploy or install scripts": "a build, deploy or install script",
  "secrets or credentials": "a secrets or credentials file", "agent instructions": "the agent's instructions",
  "git and access control": "a git or access control file",
};
const TURN_STATUS = { ok: ["OK", "info"], failed: ["Failed", "danger"], stopped: ["Stopped", "warning"] };
const FILTERS = [
  ["all", "All", {}], ["problems", "Problems", { problems: true }], ["model", "Model calls", { type: "model_call" }],
  ["tools", "Tools", { type: "tool_started,tool_done" }], ["files", "Files & commits", { type: "file,commit" }],
];
const PAGE = 500;

// 840 ms, 12.4 s, 3 min 05 s
export function duration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${int(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;
}
// a model call's cost: cents, or a fraction of a cent when that's all it was
const cost = (n) => usd(n, n > 0 && n < 0.01 ? 4 : 2);
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "");
const inputTokens = (e) => (e.input_tokens || 0) + (e.cache_read_tokens || 0) + (e.cache_write_tokens || 0) + (e.cache_write_1h_tokens || 0);
export const isProblem = (e) => (e.type === "tool_done" && e.ok === false) || e.type === "error" || e.type === "model_refused"
  || (e.type === "model_call" && (e.status || 200) >= 400);
const inTurn = (f) => (f.turn != null ? ` in turn ${f.turn}` : " between turns");

// What a finding (session_events.findings) says, in plain words
export function findingText(f) {
  switch (f.kind) {
    case "tool_failure_streak":
      return `${int(f.count)} ${f.tool && f.tool !== "other" ? f.tool : "tool"} calls failed in a row${inTurn(f)}`;
    case "costly_turn":
      return `Turn ${f.turn} cost ${cost(f.cost_usd)}, ${Math.round(f.cost_usd / f.median_usd)}× the session's usual turn`;
    case "model_error":
      return `${f.count === 1 ? "A model call" : `${int(f.count)} model calls`} failed (${f.status})${inTurn(f)}`;
    case "refused":
      return `${REFUSAL_WORDS[f.category] || REFUSAL_WORDS.other}: ${f.count === 1 ? "a model call was refused" : `${int(f.count)} model calls were refused`}`;
    case "error":
      return `${ERROR_WORDS[f.category] || ERROR_WORDS.other}${f.count > 1 ? ` (${int(f.count)} times)` : ""}${inTurn(f)}`;
    case "sensitive_path": {
      const paths = f.paths || [];
      const kinds = (f.categories || [f.category]).map((c) => SENSITIVE_WORDS[c] || `a sensitive file (${c})`);
      return (
        <>Edited {kinds.join(" and ")}: <span className="font-mono">{paths[0]}</span>
          {paths.length > 1 && ` and ${plural(paths.length - 1, "more")}`}</>
      );
    }
    case "unfinished_turn":
      return `Turn ${f.turn} never ended`;
    default:
      return (f.kind || "").replace(/_/g, " ");
  }
}

// "OK", "Failed", "Didn't finish": a tool call's outcome as a word with its icon
function Outcome({ done }) {
  if (done.unfinished) return <span className="inline-flex items-center gap-1 text-gr-warning"><CircleDashed className="w-3.5 h-3.5" aria-hidden="true" />didn't finish</span>;
  if (done.ok) return <span className="inline-flex items-center gap-1 text-gr-success"><Check className="w-3.5 h-3.5" aria-hidden="true" />ok</span>;
  return <span className="inline-flex items-center gap-1 text-gr-danger"><X className="w-3.5 h-3.5" aria-hidden="true" />failed</span>;
}

const dot = (parts) => parts.filter((p) => p != null && p !== false && p !== "").reduce((a, p, i) => (i ? [...a, " · ", p] : [p]), []);

// A row's cells: its type chip, what it is, its numbers (mono), and what it cost
function describe(row, ctx) {
  const e = row.e;
  switch (row.kind) {
    case "tool": {
      const { start, done } = row;
      const first = start || done;
      return {
        chip: "Tool",
        main: <span className="inline-flex flex-wrap items-center gap-2">{first.tool || "A tool"}{first.sub && <Tag tone="muted">Subagent</Tag>}</span>,
        metrics: done ? dot([<Outcome key="o" done={done} />, done.ms != null && duration(done.ms), done.exit_code != null && `exit ${done.exit_code}`])
          : ctx.outcomes && first.turn === ctx.running ? "running" : null,
      };
    }
    case "model_call": {
      const failed = (e.status || 200) >= 400;
      // `via`: a call the platform made for the session and billed to it, outside any turn (a change report's AI
      // summary); without it, a call with no turn came between turns (investigate.py proxy_turns)
      const via = VIA_WORDS[e.via] || (e.via && VIA_WORDS.other);
      return {
        chip: "Model",
        tone: failed ? "danger" : null,
        main: (
          <span className={cn("inline-flex flex-wrap items-center gap-2", failed && "text-gr-danger")}>
            {failed && <CircleX className="w-3.5 h-3.5" aria-hidden="true" />}{via || e.model || "Model call"}{failed && ` failed (${e.status})`}
            {e.turn == null && !e.via && <Tag tone="warning">Between turns</Tag>}
            {e.aborted && e.estimated && <Tag tone="warning">Cut off</Tag>}{e.estimated && <Tag tone="muted">Estimated</Tag>}
          </span>
        ),
        // a failed call used no tokens and cost nothing: only how long it took
        metrics: failed && !inputTokens(e) && !e.output_tokens ? dot([via && e.model, duration(e.ms), e.stop_reason && STOP_WORDS[e.stop_reason]])
          : dot([via && e.model, `in ${int(inputTokens(e))}`, `out ${int(e.output_tokens)}`, duration(e.ms), e.stop_reason && STOP_WORDS[e.stop_reason]]),
        right: failed && !e.cost_usd ? "—" : cost(e.cost_usd),
      };
    }
    case "file":
      return { chip: "File", main: <span className="font-mono">{e.path}</span>, metrics: <Lines added={e.added} removed={e.removed} /> };
    case "commit":
      return {
        chip: "Commit",
        main: <span className="font-mono">{(e.sha || "").slice(0, 7)}</span>,
        metrics: dot([plural(e.files || 0, "file"), e.pushed === false ? <span key="p" className="text-gr-warning">not pushed</span> : "pushed",
                      e.outside > 0 && `${int(e.outside)} put back from outside its folders`]),
      };
    case "error":
      return {
        chip: "Error", tone: "danger",
        main: <span className="inline-flex items-center gap-1.5 text-gr-danger"><TriangleAlert className="w-3.5 h-3.5" aria-hidden="true" />{ERROR_WORDS[e.category] || ERROR_WORDS.other}</span>,
        metrics: e.server ? `server ${e.server}` : null,
      };
    case "model_refused":
      return {
        chip: "Refused", tone: "danger",
        main: <span className="inline-flex items-center gap-1.5 text-gr-danger"><CircleX className="w-3.5 h-3.5" aria-hidden="true" />{REFUSAL_WORDS[e.category] || REFUSAL_WORDS.other}</span>,
        metrics: "a model call was refused",
      };
    case "session":
      return { chip: "Session", main: SESSION_WORDS[e.action] || SESSION_WORDS.other };
    case "turn_started":
      return {
        chip: "Turn", main: `Turn ${e.turn} started`,
        metrics: dot([ENGINE_LABELS[e.engine] || e.engine, e.model, e.effort && `${e.effort} effort`, e.plan_mode && "plan mode",
                      e.runner_version && `runner ${e.runner_version}`]),
      };
    case "turn_ended": {
      const [word] = TURN_STATUS[e.status] || ["Ended"];
      return {
        chip: "Turn", main: `Turn ${e.turn} ${word === "OK" ? "ended" : word.toLowerCase()}`,
        metrics: dot([duration(e.ms), plural(e.tools || 0, "tool"), e.tool_failures > 0 && `${int(e.tool_failures)} failed`,
                      e.errors > 0 && plural(e.errors, "error"), plural(e.files || 0, "file"), plural(e.commits || 0, "commit")]),
      };
    }
    case "engine_usage":
      return {
        chip: "Engine",
        main: <span className="inline-flex flex-wrap items-center gap-2 text-muted-foreground">The engine's own count<Tag tone="muted">Not billed</Tag></span>,
        metrics: dot([`in ${int(e.input_total)}`, `out ${int(e.output_tokens)}`, e.steps != null && plural(e.steps, "step"), e.ms != null && duration(e.ms)]),
      };
    case "truncated":
      return { chip: "Log", main: <span className="text-gr-warning">Too many events in this turn; the rest weren't kept.</span> };
    default:
      return { chip: row.kind, main: "—" };
  }
}

const ROW = "grid grid-cols-[72px_80px_minmax(0,1.15fr)_minmax(0,1fr)_72px] gap-3 items-baseline px-4 py-2";

function EventRow({ row, ctx, highlighted }) {
  const d = describe(row, ctx);
  const time = (row.start || row.e || row.done).ts;
  return (
    <div id={`ev-${row.key}`} tabIndex={-1} data-testid={`event-${row.kind}`}
      className={cn(ROW, "border-b last:border-b-0 scroll-mt-24 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        highlighted && "bg-gr-hover-strong")}>
      <span className="font-mono text-sm tabular-nums text-muted-foreground">{clock(time)}</span>
      <span><Chip className={cn(d.tone === "danger" && "text-gr-danger")}>{d.chip}</Chip></span>
      <span className="min-w-0 [overflow-wrap:anywhere]">{d.main}</span>
      <span className="min-w-0 font-mono text-sm tabular-nums text-muted-foreground [overflow-wrap:anywhere]">{d.metrics}</span>
      <span className="text-right font-mono text-sm tabular-nums">{d.right}</span>
    </div>
  );
}

function TurnHeader({ n, t, running, open, onToggle, problem }) {
  const [label, tone] = n === running ? ["Running", "info"] : TURN_STATUS[t?.status] || ["Didn't end", "danger"];
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`turn-${n}`} data-testid={`turn-${n}`}
      className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-left bg-gr-surface-2 border-b transition-colors hover:bg-gr-hover-strong focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring">
      {open ? <ChevronDown className="w-4 h-4 shrink-0" aria-hidden="true" /> : <ChevronRight className="w-4 h-4 shrink-0" aria-hidden="true" />}
      <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Turn {n}</span>
      <StatusBadge label={label} tone={tone} />
      {t && (
        <span className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-sm tabular-nums text-muted-foreground">
          <span>{duration(t.ms)}</span><span>{cost(t.cost_usd)}</span><span>{plural(t.model_calls, "model call")}</span>
          <span>{plural(t.tool_calls, "tool")}</span>
          {t.tool_failures > 0 && <span className="text-gr-danger">{int(t.tool_failures)} failed</span>}
          {t.model_errors > 0 && <span className="text-gr-danger">{plural(t.model_errors, "model call")} failed</span>}
          {t.refused > 0 && <span className="text-gr-danger">{int(t.refused)} refused</span>}
          {t.errors > 0 && <span className="text-gr-danger">{plural(t.errors, "error")}</span>}
        </span>
      )}
      {problem && !open && <span className="ml-auto text-sm text-gr-danger">Has problems</span>}
    </button>
  );
}

// The rows, tool calls paired by their number in the turn (tool_started + tool_done), then grouped: one block per turn,
// at its first event, and anything outside a turn (session actions, refusals between turns) on its own, in seq order
function layout(events) {
  const key = (e) => `${e.turn}:${e.call}`;
  const done = new Map(events.filter((e) => e.type === "tool_done" && e.call != null).map((e) => [key(e), e]));
  const started = new Set(events.filter((e) => e.type === "tool_started" && e.call != null).map(key));
  const rows = [];
  for (const e of events) {
    if (e.type === "tool_done" && e.call != null && started.has(key(e))) continue;  // shown with its start
    if (e.type === "tool_started") {
      const d = e.call != null ? done.get(key(e)) : null;
      rows.push({ key: e.seq, seqs: d ? [e.seq, d.seq] : [e.seq], turn: e.turn, kind: "tool", start: e, done: d });
    } else if (e.type === "tool_done") {
      rows.push({ key: e.seq, seqs: [e.seq], turn: e.turn, kind: "tool", start: null, done: e });
    } else {
      rows.push({ key: e.seq, seqs: [e.seq], turn: e.turn, kind: e.type, e });
    }
  }
  const blocks = [];
  const groups = new Map();
  const bySeq = new Map();
  for (const r of rows) {
    r.seqs.forEach((s) => bySeq.set(s, r));
    if (r.turn == null) { blocks.push({ row: r }); continue; }
    if (!groups.has(r.turn)) {
      const g = { n: r.turn, rows: [], problem: false };
      groups.set(r.turn, g);
      blocks.push(g);
    }
    const g = groups.get(r.turn);
    g.rows.push(r);
    g.problem = g.problem || [r.e, r.start, r.done].some((e) => e && isProblem(e));
  }
  return { blocks, bySeq };
}

function LogCheck({ chain }) {
  if (chain.valid) return <span title={`${int(chain.count)} events, each linked to the one before it`}><StatusBadge label="Log complete" tone="info" /></span>;
  const missing = chain.gaps.reduce((a, g) => a + (g.to - g.from + 1), 0);
  return <StatusBadge label={`${int(missing)} missing · ${int(chain.edited.length)} edited`} tone="danger" />;
}

// `audience`: "owner" or "coder" (the same page, worded for each); `person`: the independent professional's name
export default function InvestigateTimeline({ sid, audience, back, person, names }) {
  const qc = useQueryClient();
  const inv = useQuery({
    queryKey: ["investigation", sid],
    queryFn: async () => (await api.get(`/sessions/${sid}/investigation`)).data,
    // a turn is running: the numbers now and then, the events as they come (below), and again when a turn ends
    refetchInterval: (q) => (q.state.data?.session?.current_turn ? 30000 : false),
  });
  const running = inv.data?.session?.current_turn?.n ?? null;
  const [filter, setFilter] = useState("all");
  const params = FILTERS.find(([k]) => k === filter)[2];
  const events = useInfiniteQuery({
    queryKey: ["session-events", sid, filter],
    queryFn: async ({ pageParam }) => (await api.get(`/sessions/${sid}/events`, { params: { ...params, after: pageParam, limit: PAGE } })).data,
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_after ?? undefined,
    enabled: !!inv.data,
  });
  const list = useMemo(() => (events.data?.pages || []).flatMap((p) => p.events), [events.data]);
  // While a turn runs, ask only for what came after the last event loaded (every 5 s) and add it to the last page.
  // Not while earlier pages are still to load: new events come with them. A turn starting or ending refreshes the
  // numbers.
  const lastSeq = list.length ? list[list.length - 1].seq : 0;
  const allLoaded = !!events.data && !events.hasNextPage;
  useEffect(() => {
    if (running == null || !allLoaded) return undefined;
    let stopped = false;
    const tick = setInterval(async () => {
      try {
        const page = (await api.get(`/sessions/${sid}/events`, { params: { ...params, after: lastSeq, limit: PAGE } })).data;
        if (stopped || !page.events.length) return;
        qc.setQueryData(["session-events", sid, filter], (old) => {
          if (!old?.pages?.length) return old;
          const pages = [...old.pages];
          const last = pages[pages.length - 1];
          pages[pages.length - 1] = { ...last, events: [...last.events, ...page.events], next_after: page.next_after };
          return { ...old, pages };
        });
        if (page.events.some((e) => e.type === "turn_ended" || e.type === "turn_started")) {
          qc.invalidateQueries({ queryKey: ["investigation", sid] });
        }
      } catch {
        // the next tick tries again
      }
    }, 5000);
    return () => { stopped = true; clearInterval(tick); };
  }, [running, allLoaded, lastSeq, sid, filter, params, qc]);
  const { blocks, bySeq } = useMemo(() => layout(list), [list]);
  const perTurn = useMemo(() => Object.fromEntries((inv.data?.summary?.per_turn || []).map((t) => [t.turn, t])), [inv.data]);

  // a turn starts open when it had a problem, didn't end or is running; any filter but All opens them all
  const [toggled, setToggled] = useState({});
  const problemTurn = (g) => {
    const t = perTurn[g.n];
    return g.problem || (t && (t.tool_failures > 0 || t.model_errors > 0 || t.refused > 0 || t.errors > 0 || t.status === "failed"
                               || (t.status == null && g.n !== running)));
  };
  const isOpen = (g) => toggled[g.n] ?? (filter !== "all" || g.n === running || problemTurn(g));

  // a finding points at an event (seq): load the pages up to it, open its turn, scroll to it and mark it
  const [target, setTarget] = useState(null);
  const [marked, setMarked] = useState(null);
  const [missing, setMissing] = useState(false);
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isFetching } = events;
  useEffect(() => {
    if (target == null || !events.data || isFetching) return;
    const row = bySeq.get(target);
    if (row) {
      const el = document.getElementById(`ev-${row.key}`);
      if (!el) {  // its turn is closed: open it, then come back once it's drawn
        if (row.turn != null && !toggled[row.turn]) setToggled((t) => ({ ...t, [row.turn]: true }));
        else setTarget(null);
        return;
      }
      const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" });
      el.focus({ preventScroll: true });
      setMarked(row.key);
      setTarget(null);
    } else if (hasNextPage) {
      if (!isFetchingNextPage) fetchNextPage();
    } else if (filter !== "all") {
      setFilter("all");  // the filter hides it
    } else {
      setMissing(true);  // not in the log: the log check says why
      setTarget(null);
    }
  }, [target, events.data, isFetching, bySeq, toggled, hasNextPage, isFetchingNextPage, fetchNextPage, filter]);
  const reveal = (seq) => { setMissing(false); setTarget(seq); };

  const header = (status) => (
    <PageHeader eyebrow={audience === "coder" ? "Your session · Metadata only" : "Investigate · Metadata only"}
      title={audience === "coder" ? "What your founder sees." : `What happened in session ${inv.data?.session?.short_id || ""}.`}
      status={status} testid="investigate-header" />
  );
  const backLink = back && (
    <Link to={back.to} className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm">
      <ArrowLeft className="w-3.5 h-3.5" />{back.label}
    </Link>
  );
  if (inv.isLoading) return <Page>{backLink}{header()}<Loading /></Page>;
  if (inv.error) return (
    <Page>{backLink}{header()}
      <Card className="px-5 py-6 text-muted-foreground">{inv.error?.response?.status === 404 ? "This session doesn't exist, or isn't one you can see." : apiErr(inv.error)}</Card>
    </Page>
  );

  const { session: s, summary: sum, findings, chain, runner } = inv.data;
  const problems = findings.filter((f) => f.severity === "problem").length;
  const warnings = findings.length - problems;
  const status = [
    running != null && `Turn ${running} is running.`,
    !sum.turns ? "No turns yet." : findings.length
      ? `${plural(problems, "problem")}${warnings ? ` and ${plural(warnings, "warning")}` : ""} in ${plural(sum.turns, "turn")}.`
      : `No findings in ${plural(sum.turns, "turn")}.`,
  ].filter(Boolean).join(" ");
  const missingCount = chain.gaps.reduce((a, g) => a + (g.to - g.from + 1), 0);
  const ctx = { running, outcomes: runner.tool_outcomes };

  return (
    <Page data-testid={audience === "coder" ? "coder-timeline" : "owner-investigate-session"}>
      {backLink}
      {header(status)}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="investigate-meta">
        {audience === "coder" && <span className="font-display text-[15px] tracking-[0.02em] font-semibold tabular-nums">Session {s.short_id}</span>}
        <EngineBadge engine={s.engine} />
        <span className="text-muted-foreground">{s.model}</span>
        {s.review_ready ? <StatusBadge label="In review" tone="warning" /> : <StatusBadge status={s.status} />}
        <LogCheck chain={chain} />
        {person && <span className="text-muted-foreground">{person}</span>}
      </div>

      <StatStrip items={[
        { label: "Spent", value: cost(s.spent_usd), sub: s.budget_cap_usd ? `of ${usd(s.budget_cap_usd)}` : "No session cap" },
        { label: "Turns", value: int(sum.turns), sub: running != null ? `Turn ${running} running` : sum.last_ts ? `Last ${ago(sum.last_ts).toLowerCase()}` : "None yet" },
        { label: "Model calls", value: int(sum.model_calls),
          sub: sum.model_errors ? <span className="text-gr-danger">{int(sum.model_errors)} failed</span> : `in ${int(sum.input_tokens)} · out ${int(sum.output_tokens)}` },
        { label: "Tool calls", value: int(sum.tool_calls),
          sub: !sum.tool_outcomes ? "Outcomes not reported" : sum.tool_failures ? <span className="text-gr-danger">{int(sum.tool_failures)} failed</span> : "None failed" },
        { label: "Files", value: int(sum.files), sub: plural(sum.commits, "commit") },
      ]} />

      {!runner.tool_outcomes && (
        <Card className="px-4 py-3 flex items-start gap-2.5 text-gr-ink-secondary" data-testid="runner-note">
          <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span className="[text-wrap:pretty]">Tool outcomes need runner 1.20.0. This session shows tools by name only.</span>
        </Card>
      )}
      {!chain.valid && (
        <Card className="px-4 py-3 flex items-start gap-2.5" data-testid="chain-broken">
          <ShieldX className="w-4 h-4 mt-0.5 shrink-0 text-gr-danger" aria-hidden="true" />
          <span className="[text-wrap:pretty]">
            <span className="text-gr-danger font-semibold">This log isn't whole. </span>
            {missingCount > 0 && `${plural(missingCount, "event")} ${missingCount === 1 ? "is" : "are"} missing. `}
            {chain.edited.length > 0 && `${plural(chain.edited.length, "event")} changed after ${chain.edited.length === 1 ? "it was" : "they were"} written. `}
            Each event is linked to the one before it, so nothing can be removed or edited without showing here. Contact {APP_NAME} support.
          </span>
        </Card>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="findings-title">
        <SectionTitle note="Fixed rules, no AI. Pick one to see it in the timeline.">
          <span id="findings-title">Findings</span>
        </SectionTitle>
        {findings.length ? (
          <div className="flex flex-col gap-px bg-gr-hairline rounded-[10px] overflow-hidden" data-testid="findings">
            {findings.map((f, i) => (
              <button key={`${f.kind}-${f.seq}-${i}`} type="button" disabled={f.seq == null} onClick={() => reveal(f.seq)}
                data-testid={`finding-${f.kind}`}
                className="w-full text-left bg-card flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring disabled:hover:bg-card">
                {f.severity === "problem"
                  ? <CircleX className="w-4 h-4 mt-0.5 shrink-0 text-gr-danger" aria-label="Problem" />
                  : <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-gr-warning" aria-label="Warning" />}
                <span className="flex-1 min-w-0 [overflow-wrap:anywhere] [text-wrap:pretty]">{findingText(f)}</span>
                {f.turn != null && <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">Turn {f.turn}</span>}
                {f.seq != null && <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
              </button>
            ))}
          </div>
        ) : (
          <Card className="px-5 py-4 text-muted-foreground [text-wrap:pretty]" data-testid="no-findings">
            No findings: no failed tools or model calls, refusals, errors, unusually costly turns, sensitive files or turns that didn't end.
          </Card>
        )}
        {missing && <span className="text-sm text-gr-warning" role="status">That event isn't in the log any more.</span>}
      </section>

      <InvestigateIntegrity sid={sid} integrity={inv.data.integrity} engine={s.engine} reveal={reveal}
        reportLink={audience === "coder" ? null : (id) => `/founder/reports?report=${id}`} />

      <InvestigateReports sid={sid} shortId={s.short_id} turns={sum.turns} audience={audience} names={names} />

      <section className="flex flex-col gap-3" aria-labelledby="timeline-title">
        <SectionTitle note={`${plural(chain.count, "event")} in order`}><span id="timeline-title">Timeline</span></SectionTitle>
        <Segmented label="Show" className="self-start" options={FILTERS.map(([k, label]) => [k, label])} value={filter}
          onChange={(v) => { setFilter(v); setMarked(null); }} testid="timeline-filter" />
        {events.isLoading ? <Loading /> : events.error ? (
          <Card className="px-5 py-6 text-muted-foreground">{apiErr(events.error)}</Card>
        ) : !list.length ? (
          <Card className="px-5 py-6 text-muted-foreground" data-testid="timeline-empty">
            {filter === "all" ? "Nothing recorded yet." : filter === "problems" ? "No problems in this session." : "Nothing of this kind yet."}
          </Card>
        ) : (
          // its own scroller: at phone width the rows keep their columns and scroll sideways here, not the page
          <div className="bg-card rounded-[10px] overflow-x-auto" data-testid="investigate-timeline">
            <div className="min-w-[760px] text-sm">
              {blocks.map((b) => (b.row ? (
                <EventRow key={b.row.key} row={b.row} ctx={ctx} highlighted={marked === b.row.key} />
              ) : (
                <div key={`turn-${b.n}`}>
                  <TurnHeader n={b.n} t={perTurn[b.n]} running={running} open={isOpen(b)} problem={problemTurn(b)}
                    onToggle={() => setToggled((t) => ({ ...t, [b.n]: !isOpen(b) }))} />
                  {isOpen(b) && (
                    <div id={`turn-${b.n}`}>
                      {b.rows.map((r) => <EventRow key={r.key} row={r} ctx={ctx} highlighted={marked === r.key} />)}
                    </div>
                  )}
                </div>
              )))}
            </div>
          </div>
        )}
        {hasNextPage && (
          <Button variant="outline" className="self-start" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} data-testid="timeline-more">
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
      </section>

      <PrivacyNote>
        {audience === "coder"
          ? "Your founder sees this page too. It shows what happened in the session: turns, model calls, tools, files and commits. It never shows what you wrote, what the agent replied, or what a command contained."
          : "This page shows what happened in the session: turns, model calls, tools, files and commits. It never shows what was said, what the agent replied, or what a command contained."}
      </PrivacyNote>
    </Page>
  );
}
